/**
 * Style File composer — backs `/merchandising/styles/new`.
 *
 * The anchor garment surface's create flow (build-plan Inc 1, M2). Mirrors the
 * invoice composer's shape (centered `max-w-screen-2xl`, numbered section cards
 * in a form column + a sticky summary aside): captures the header (style code,
 * buyer, garment type, name, currency), the optional buyer PO + timeline, and
 * the colour × size ratio grid, then `styles.create` opens it as a `draft`. The
 * operator confirms the PO from the detail page (`confirmPo`), Draft → PO
 * confirmed.
 *
 * Picking a garment type seeds the grid's size columns from that type's template
 * (`styles.templates`); colours are added row by row, sizes via the Add-size
 * dialog. The grid state is local (a 2-D structure is awkward in
 * react-hook-form); it flattens to the wire's flat cell list at submit, dropping
 * empty (qty 0) cells.
 *
 * @see apps/web/src/features/invoices/composer-page.tsx — the layout we mirror
 * @see prototype/garment/style-file.html — V1.1 Style File baseline
 * @see packages/api-contract/src/procedures/styles.ts
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check, Loader2, Palette, Ruler, Trash2, X } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Combobox,
  Input,
  PromptDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { buildGarmentTypeGroups, styleCodePrefixFor } from "../lib/garment-type.js";
import { GarmentFlat } from "./design-studio/garment-flat.js";

interface ColourRow {
  colour: string;
  colourHex: string;
}

/** FOB currencies an Indian garment exporter actually quotes in. */
const CURRENCY_OPTIONS = [
  { code: "INR", label: "INR · Indian rupee" },
  { code: "USD", label: "USD · US dollar" },
  { code: "EUR", label: "EUR · Euro" },
  { code: "GBP", label: "GBP · Pound sterling" },
  { code: "AED", label: "AED · UAE dirham" },
] as const;

const FIELD =
  "h-9 w-full rounded-sm border border-line bg-paper px-3 text-sm text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100 disabled:opacity-60";

/** A suggested internal style code from the garment type + a random suffix.
 *  The operator can accept or overtype it — style codes are usually buyer-owned. */
function suggestStyleRef(prefix: string): string {
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${suffix}`;
}

export function StyleComposerPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const templates = trpc.styles.templates.useQuery();
  const buyers = trpc.customers.list.useQuery({ limit: 100 });
  const garmentTypeGroups = useMemo(
    () => buildGarmentTypeGroups(templates.data ?? []),
    [templates.data],
  );

  // ── Header state ──────────────────────────────────────────────
  const [styleRef, setStyleRef] = useState("");
  const [name, setName] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [garmentType, setGarmentType] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [exFactoryDate, setExFactoryDate] = useState("");

  // ── Ratio grid state ──────────────────────────────────────────
  const [sizes, setSizes] = useState<string[]>([]);
  const [colours, setColours] = useState<ColourRow[]>([{ colour: "", colourHex: "" }]);
  // qty keyed by `${colourIndex}|${size}` → operator string.
  const [qty, setQty] = useState<Record<string, string>>({});

  // ── Add-size dialog ───────────────────────────────────────────
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);

  const create = trpc.styles.create.useMutation({
    onSuccess: async () => {
      await utils.styles.list.invalidate();
    },
  });

  const onPickType = (key: string) => {
    setGarmentType(key);
    const tpl = templates.data?.find((t) => t.key === key);
    if (tpl && sizes.length === 0) setSizes([...tpl.sizes]);
  };

  const rowTotalFor = (ci: number) =>
    sizes.reduce((sum, s) => {
      const n = Number.parseInt(qty[`${ci}|${s}`] ?? "", 10);
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);

  const totalQty = useMemo(() => {
    let sum = 0;
    for (const v of Object.values(qty)) {
      const n = Number.parseInt(v, 10);
      if (Number.isFinite(n) && n > 0) sum += n;
    }
    return sum;
  }, [qty]);

  // Per-colourway totals for the summary aside (named colours with quantity).
  const colourSummaries = useMemo(
    () =>
      colours
        .map((c, ci) => ({
          colour: c.colour.trim(),
          hex: c.colourHex || "#1e3a8a",
          total: sizes.reduce((sum, s) => {
            const n = Number.parseInt(qty[`${ci}|${s}`] ?? "", 10);
            return sum + (Number.isFinite(n) && n > 0 ? n : 0);
          }, 0),
        }))
        .filter((c) => c.colour !== "" && c.total > 0),
    [colours, qty, sizes],
  );

  const namedColours = colours.filter((c) => c.colour.trim()).length;
  // Dominant colourway (biggest quantity) tints the garment preview; falls back
  // to the first picked swatch, then to the line-art default once nothing's set.
  const heroColour = useMemo(() => {
    if (colourSummaries.length > 0) {
      return [...colourSummaries].sort((a, b) => b.total - a.total)[0]?.hex;
    }
    const firstNamed = colours.find((c) => c.colour.trim());
    return firstNamed?.colourHex || undefined;
  }, [colourSummaries, colours]);
  const typeLabel = templates.data?.find((t) => t.key === garmentType)?.label;
  const buyerName = buyers.data?.items.find((b) => b.id === buyerId)?.name;

  const ready =
    styleRef.trim() !== "" &&
    name.trim() !== "" &&
    buyerId !== "" &&
    garmentType !== "" &&
    sizes.length > 0 &&
    totalQty > 0;

  const addColour = () => setColours((cs) => [...cs, { colour: "", colourHex: "" }]);
  const removeColour = (idx: number) => setColours((cs) => cs.filter((_, i) => i !== idx));
  const removeSize = (size: string) => setSizes((s) => s.filter((x) => x !== size));

  const addSize = (next: string) => {
    if (sizes.includes(next)) {
      toast.error("That size already exists", { description: `“${next}” is already a column.` });
      throw new Error("duplicate size"); // keep the dialog open
    }
    setSizes((s) => [...s, next]);
  };

  const buildLines = () => {
    const lines: Array<{ colour: string; colourHex: string | null; size: string; qty: number }> = [];
    colours.forEach((row, ci) => {
      if (row.colour.trim() === "") return;
      for (const size of sizes) {
        const raw = qty[`${ci}|${size}`];
        const n = raw ? Number.parseInt(raw, 10) : 0;
        if (Number.isFinite(n) && n > 0) {
          lines.push({
            colour: row.colour.trim(),
            colourHex: row.colourHex.trim() || null,
            size,
            qty: n,
          });
        }
      }
    });
    return lines;
  };

  const submit = async () => {
    const lines = buildLines();
    if (lines.length === 0) {
      toast.error("Enter at least one quantity", {
        description: "Fill a colour name and at least one size quantity.",
      });
      return;
    }
    try {
      const result = await create.mutateAsync({
        styleRef: styleRef.trim(),
        buyerId,
        garmentType,
        name: name.trim(),
        ...(currency.trim() ? { currency: currency.trim().toUpperCase() } : {}),
        ...(poNumber.trim() ? { poNumber: poNumber.trim() } : {}),
        ...(poDate ? { poDate } : {}),
        ...(exFactoryDate ? { exFactoryDate } : {}),
        lines,
      });
      toast.success(`Style ${styleRef.trim()} created`, {
        description: `Job ${result.jobNo} assigned — ready for Costing & BOM.`,
      });
      void navigate({ to: "/merchandising/styles/$styleId/overview", params: { styleId: result.id } });
    } catch (err) {
      toast.error("Couldn't create style file", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  };

  return (
    <div className="flex min-h-full flex-col bg-bg">
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
        <Link
          to="/merchandising/styles"
          aria-label="Back to style files"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold leading-tight tracking-tight text-ink">New style file</h1>
          <p className="mt-0.5 text-xs text-muted">
            Open a Style File for one buyer PO line — capture the order and the colour × size ratio.
          </p>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-1 gap-5 px-6 py-5 xl:grid-cols-5">
        {/* Form column */}
        <div className="flex min-w-0 flex-col gap-4 xl:col-span-3">
          {buyers.error ? (
            <Alert intent="bad" title="Couldn't load buyers" description={buyers.error.message} />
          ) : null}
          {create.error ? (
            <Alert intent="bad" title="Couldn't create style" description={create.error.message} />
          ) : null}

          {/* 1 · Style details */}
          <Card>
            <SectionHeading
              step={1}
              title="Style details"
              description="The basics that identify this order across every document."
            />
            <CardBody className="grid gap-3.5 sm:grid-cols-2">
              <Field label="Style code" required hint="Your internal reference — buyer-owned, or suggest one.">
                <div className="flex gap-1.5">
                  <Input
                    value={styleRef}
                    onChange={(e) => setStyleRef(e.currentTarget.value)}
                    placeholder="FT-SH-102"
                    className="h-9 flex-1 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setStyleRef(suggestStyleRef(styleCodePrefixFor(templates.data ?? [], garmentType)))}
                    className="h-9 flex-shrink-0 rounded-md border border-line px-2.5 text-xs font-medium text-ink-2 transition hover:border-sky-300 hover:text-sky-700"
                  >
                    Suggest
                  </button>
                </div>
              </Field>
              <Field label="Style name" required hint="What the buyer calls it.">
                <Input
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  placeholder="Men's Crew Tee"
                  className="h-9 text-sm"
                />
              </Field>
              <Field label="Buyer" required hint="Who placed the PO.">
                <Select value={buyerId} onValueChange={setBuyerId} disabled={buyers.isLoading}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder={buyers.isLoading ? "Loading…" : "Select buyer"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(buyers.data?.items ?? []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-[1fr_auto] gap-3">
                <Field label="Garment type" required hint="Seeds the size columns.">
                  <Combobox
                    value={garmentType}
                    onValueChange={onPickType}
                    groups={garmentTypeGroups}
                    placeholder={templates.isLoading ? "Loading…" : "Select type"}
                    searchPlaceholder="Search garment types…"
                    emptyMessage="No matching garment type."
                    disabled={templates.isLoading}
                  />
                </Field>
                <Field label="Currency" hint="FOB">
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="h-9 w-[88px] font-mono text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* 2 · Buyer PO & timeline */}
          <Card>
            <SectionHeading
              step={2}
              title="Buyer PO & timeline"
              description="Informational only — PO confirmation happens on the Order, not here. Add it now if you have it, or leave it for later."
            />
            <CardBody className="grid gap-3.5 sm:grid-cols-3">
              <Field label="PO number" hint="From the buyer's purchase order.">
                <Input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.currentTarget.value)}
                  placeholder="4501-22387"
                  className="h-9 font-mono text-sm"
                />
              </Field>
              <Field label="PO date">
                <input
                  type="date"
                  value={poDate}
                  onChange={(e) => setPoDate(e.currentTarget.value)}
                  className={`${FIELD} font-mono`}
                />
              </Field>
              <Field label="Ex-factory" hint="Drives the T&A plan.">
                <input
                  type="date"
                  value={exFactoryDate}
                  onChange={(e) => setExFactoryDate(e.currentTarget.value)}
                  className={`${FIELD} font-mono`}
                />
              </Field>
            </CardBody>
          </Card>

          {/* 3 · Colour × size ratio */}
          <Card>
            <SectionHeading
              step={3}
              title="Colour × size ratio"
              description="One row per colourway, one column per size. Key the buyer's quantity into each cell."
            />
            <CardBody className="flex flex-col gap-3">
              <div className="flex items-center justify-end gap-2">
                <Button type="button" intent="ghost" className="border border-line bg-paper" onClick={() => setSizeDialogOpen(true)} disabled={garmentType === ""}>
                  <Ruler size={14} /> Add size
                </Button>
                <Button type="button" intent="ghost" className="border border-line bg-paper" onClick={addColour} disabled={garmentType === ""}>
                  <Palette size={14} /> Add colour
                </Button>
              </div>

              {sizes.length === 0 ? (
                <div className="flex flex-col items-center gap-1 rounded-md border border-dashed border-line bg-bg px-4 py-12 text-center">
                  <Ruler className="mb-1 text-muted" size={20} />
                  <p className="text-sm font-medium text-ink-2">No sizes yet</p>
                  <p className="max-w-sm text-xs text-muted">
                    Pick a <span className="font-medium text-ink-2">garment type</span> above to auto-fill the standard
                    size run, or add sizes one at a time.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border border-line">
                  <table className="w-full min-w-[560px] border-collapse text-sm">
                    <thead>
                      <tr className="bg-raised text-xs uppercase tracking-wide text-muted">
                        <th className="sticky left-0 z-10 bg-raised px-3 py-2.5 text-left font-medium">Colourway</th>
                        {sizes.map((s) => (
                          <th key={s} className="px-2 py-2.5 text-center font-medium">
                            <div className="inline-flex items-center gap-1">
                              <span className="font-mono normal-case text-ink-2">{s}</span>
                              <button
                                type="button"
                                aria-label={`Remove size ${s}`}
                                onClick={() => removeSize(s)}
                                className="grid h-4 w-4 place-items-center rounded text-muted transition hover:bg-bad-50 hover:text-bad"
                              >
                                <X size={11} />
                              </button>
                            </div>
                          </th>
                        ))}
                        <th className="px-3 py-2.5 text-right font-medium">Row</th>
                        <th className="w-9 bg-raised" />
                      </tr>
                    </thead>
                    <tbody>
                      {colours.map((row, ci) => {
                        const rowTotal = rowTotalFor(ci);
                        return (
                          <tr key={ci} className="border-t border-line odd:bg-paper even:bg-bg/40">
                            <td className="sticky left-0 z-10 bg-inherit px-3 py-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  aria-label={`Colour swatch ${ci + 1}`}
                                  value={row.colourHex || "#1e3a8a"}
                                  onChange={(e) => {
                                    const hex = e.currentTarget.value;
                                    setColours((cs) => cs.map((c, i) => (i === ci ? { ...c, colourHex: hex } : c)));
                                  }}
                                  className="h-7 w-7 shrink-0 cursor-pointer rounded border border-line bg-paper p-0.5"
                                />
                                <Input
                                  value={row.colour}
                                  onChange={(e) => {
                                    const v = e.currentTarget.value;
                                    setColours((cs) => cs.map((c, i) => (i === ci ? { ...c, colour: v } : c)));
                                  }}
                                  placeholder="e.g. Navy"
                                  className="h-8 w-32 text-sm"
                                />
                              </div>
                            </td>
                            {sizes.map((s) => (
                              <td key={s} className="px-1.5 py-2">
                                <input
                                  inputMode="numeric"
                                  aria-label={`${row.colour || "colour"} ${s} qty`}
                                  value={qty[`${ci}|${s}`] ?? ""}
                                  placeholder="0"
                                  onChange={(e) => {
                                    const v = e.currentTarget.value.replace(/[^\d]/g, "");
                                    setQty((q) => ({ ...q, [`${ci}|${s}`]: v }));
                                  }}
                                  className="h-8 w-16 rounded-sm border border-line bg-paper px-1 text-center font-mono text-sm text-ink-2 placeholder:text-line-2 focus:border-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                />
                              </td>
                            ))}
                            <td className="px-3 py-2 text-right font-mono text-sm font-semibold text-ink">
                              {rowTotal.toLocaleString("en-IN")}
                            </td>
                            <td className="px-1 py-2 text-center">
                              {colours.length > 1 ? (
                                <button
                                  type="button"
                                  aria-label={`Remove colour ${ci + 1}`}
                                  onClick={() => removeColour(ci)}
                                  className="grid h-7 w-7 place-items-center rounded-sm text-muted transition hover:bg-bad-50 hover:text-bad"
                                >
                                  <Trash2 size={14} />
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-line bg-raised">
                        <td className="sticky left-0 z-10 bg-raised px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                          Total
                        </td>
                        {sizes.map((s) => {
                          const colTotal = colours.reduce((sum, _r, ci) => {
                            const n = Number.parseInt(qty[`${ci}|${s}`] ?? "", 10);
                            return sum + (Number.isFinite(n) && n > 0 ? n : 0);
                          }, 0);
                          return (
                            <td key={s} className="px-2 py-2.5 text-center font-mono text-sm text-ink-2">
                              {colTotal.toLocaleString("en-IN")}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2.5 text-right font-mono text-base font-bold text-ink">
                          {totalQty.toLocaleString("en-IN")}
                        </td>
                        <td className="bg-raised" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Summary aside (sticky on xl) — the style-file analog of the invoice live preview */}
        <aside className="xl:sticky xl:top-4 xl:col-span-2 xl:self-start">
          <div className="overflow-hidden rounded-md border border-line bg-paper shadow-md">
            <header className="flex items-center justify-between gap-3 border-b border-line bg-raised px-4 py-3">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                <span
                  aria-hidden
                  className={`h-2 w-2 rounded-full ${ready ? "bg-emerald-500 shadow-rail" : "bg-warn"}`}
                />
                Order summary
              </h3>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                  ready ? "bg-emerald-50 text-emerald-700" : "bg-warn-50 text-warn"
                }`}
              >
                {ready ? "Ready" : "Draft"}
              </span>
            </header>

            {/* Garment preview — line-art silhouette matching the selected
                garment type. */}
            <div className="relative border-b border-line bg-gradient-to-b from-bg to-paper px-5 pt-4 pb-3">
              <div className="pointer-events-none mx-auto flex h-40 w-full max-w-[180px] items-center justify-center">
                <GarmentFlat garmentType={garmentType} view="front" viewBox="0 40 400 420" />
              </div>
              {typeLabel || colourSummaries.length > 0 ? (
                <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5">
                  {typeLabel ? (
                    <span className="inline-flex items-center rounded-full border border-line bg-paper px-2 py-0.5 text-xs font-medium text-muted">
                      {typeLabel}
                    </span>
                  ) : null}
                  {colourSummaries.length > 0 ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-paper px-2 py-0.5 text-xs font-medium text-muted">
                      <span
                        aria-hidden
                        className="h-2 w-2 rounded-full border border-line"
                        style={{ backgroundColor: heroColour }}
                      />
                      {namedColours} colourway{namedColours === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Hero — total order */}
            <div className="border-b border-line bg-gradient-to-b from-sky-50/60 to-transparent px-5 py-4">
              <div className="text-xs font-medium uppercase tracking-wide text-muted">Total order</div>
              <div className="mt-1 flex items-end gap-1.5">
                <span className="font-mono text-3xl font-bold leading-none text-ink">
                  {totalQty.toLocaleString("en-IN")}
                </span>
                <span className="pb-1 text-sm text-muted">pcs</span>
              </div>
              <div className="mt-1.5 text-xs text-muted">
                {namedColours} colour{namedColours === 1 ? "" : "s"} × {sizes.length} size
                {sizes.length === 1 ? "" : "s"}
                {currency !== "INR" ? ` · ${currency} FOB` : ""}
              </div>
            </div>

            {/* Identity + facts */}
            <div className="border-b border-line px-5 py-4">
              <div className="font-mono text-sm font-semibold text-ink">{styleRef.trim() || "Style code"}</div>
              <div className="truncate text-sm text-ink-2">{name.trim() || "Untitled style"}</div>
              <dl className="mt-3 flex flex-col gap-2 text-sm">
                <SummaryRow label="Buyer" value={buyerName} />
                <SummaryRow label="Garment type" value={typeLabel} />
                <SummaryRow label="FOB currency" value={currency} mono />
                <SummaryRow label="Ex-factory" value={exFactoryDate ? formatDate(exFactoryDate) : undefined} />
              </dl>
            </div>

            {/* Size run */}
            {sizes.length > 0 ? (
              <div className="border-b border-line px-5 py-3.5">
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">Size run</div>
                <div className="flex flex-wrap gap-1">
                  {sizes.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center rounded border border-line bg-bg px-1.5 py-0.5 font-mono text-xs text-ink-2"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Colourway breakdown with proportion bars */}
            <div className="px-5 py-4">
              <div className="mb-2.5 text-xs font-medium uppercase tracking-wide text-muted">Colourways</div>
              {colourSummaries.length > 0 ? (
                <ul className="flex flex-col gap-3">
                  {colourSummaries.map((c) => {
                    const pct = totalQty > 0 ? Math.round((c.total / totalQty) * 100) : 0;
                    return (
                      <li key={c.colour}>
                        <div className="flex items-center gap-2 text-sm">
                          <span
                            className="h-3.5 w-3.5 shrink-0 rounded-full border border-line"
                            style={{ backgroundColor: c.hex }}
                          />
                          <span className="min-w-0 flex-1 truncate text-ink-2">{c.colour}</span>
                          <span className="font-mono font-medium text-ink">{c.total.toLocaleString("en-IN")}</span>
                          <span className="w-8 text-right font-mono text-xs text-muted">{pct}%</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: c.hex }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="rounded-md border border-dashed border-line bg-bg px-3 py-4 text-center text-xs text-muted">
                  Add a colourway and quantities to see the split.
                </p>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Sticky footer — status left, actions right (mirrors the invoice composer) */}
      <footer className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center gap-2.5 border-t border-line bg-paper px-6 py-3 shadow-lg">
        <p className="min-w-0 flex-1 text-xs text-muted">
          {ready ? (
            <>
              <strong className="text-ink">Ready to save.</strong> {totalQty.toLocaleString("en-IN")} pieces across{" "}
              {namedColours} colour{namedColours === 1 ? "" : "s"} × {sizes.length} size{sizes.length === 1 ? "" : "s"}.
            </>
          ) : (
            "Fill the style code, buyer, garment type, and at least one quantity."
          )}
        </p>
        <Button type="button" intent="ghost" onClick={() => void navigate({ to: "/merchandising/styles" })}>
          Cancel
        </Button>
        <Button type="button" disabled={!ready || create.isPending} onClick={() => void submit()}>
          {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
          Create style file
        </Button>
      </footer>

      {/* Add-size dialog — shared HTML PromptDialog (never window.prompt). */}
      <PromptDialog
        open={sizeDialogOpen}
        onOpenChange={setSizeDialogOpen}
        title="Add a size"
        description={
          <>
            Adds a new column to the ratio grid — e.g. <span className="font-mono">XXL</span>,{" "}
            <span className="font-mono">48</span>, or <span className="font-mono">12-13Y</span>.
          </>
        }
        label="Size label"
        placeholder="e.g. XXL"
        confirmLabel="Add size"
        onConfirm={addSize}
      />
    </div>
  );
}

function SectionHeading({ step, title, description }: { step: number; title: string; description?: string }) {
  return (
    <div className="border-b border-line bg-paper px-[18px] pt-3.5 pb-2.5">
      <h3 className="m-0 inline-flex items-center gap-2 text-sm font-semibold leading-tight text-ink">
        <span
          aria-hidden
          className="inline-grid h-5 w-5 shrink-0 place-items-center rounded-full bg-bg font-mono text-xs font-bold text-muted"
        >
          {step}
        </span>
        {title}
      </h3>
      {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
    </div>
  );
}

function SummaryRow({ label, value, mono }: { label: string; value?: string | undefined; mono?: boolean }) {
  const has = value != null && value !== "";
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right ${has ? "font-medium text-ink-2" : "text-line-2"} ${mono ? "font-mono" : ""}`}
      >
        {has ? value : "Not set"}
      </dd>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-2">
        {label}
        {required ? <span className="text-bad"> *</span> : null}
      </span>
      {children}
      {hint ? <span className="text-xs leading-tight text-muted">{hint}</span> : null}
    </label>
  );
}
